import { TextInput } from '@momentum-ui/react-collaboration';
import { IWebexIntContact } from '@webex/component-adapter-interfaces/dist/esm/src';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { WebexSearchContactsList } from '../WebexSearchContactsList/WebexSearchContactsList';
import { useSearchDropdown } from '../hooks/useSearchDropdown';
import useWebexClasses from '../hooks/useWebexClasses';
import { InputSource } from '../types/InputSource';
import { isMobileFeatureParityEnabledForCurrentUser } from '../utils/mobileFeatureParityConfig';
import './WebexMobileSearchContacts.styles.scss';

export interface IWebexMobileSearchContactsProps {
  label?: string;
  noContactsFoundMessage?: string;
  style?: React.CSSProperties;
  minSearchLength?: number;
  onInputChange?: (input: string) => void;
  onUserSelect?: (user: IWebexIntContact | undefined) => void;
  onDropdownHide?: () => void;
  hideDropdownSource?: boolean;
  makeCall?:(address: string, video?: boolean) => void;
  enableFeatureParitySearch?: boolean;
}

export type WebexMobileSearchContactsHandle = {
  appendValueToInput: (toAppend: string, source?: InputSource) => void;
  backSpaceAction: () => void;
  getInputValue: () => string;
  callButtonPressAction: () => void;
  clearInput: () => void;
  focusInput: () => void;
  blurInput: () => void;
};

/**
 * Description for this component.
 *
 * @param {IWebexSearchContactsProps} props Props for this component
 * @param props.style optional styles to add
 * @param props.label label to display in the search bar
 * @param props.noContactsFoundMessage text to output when no users are found
 * @param props.minSearchLength minimum text length required before searching
 * @param props.inputId id to make the search input
 */
export const WebexMobileSearchContacts = forwardRef<
WebexMobileSearchContactsHandle,
  IWebexMobileSearchContactsProps
>(
  (
    {
      label,
      noContactsFoundMessage,
      style,
      minSearchLength,
      onInputChange,
      onUserSelect,
      onDropdownHide,
      hideDropdownSource,
      makeCall,
      enableFeatureParitySearch = true,
    },
    ref
  ) => {
    const [cssClasses, sc] = useWebexClasses('search-contacts-widget');
    const [inputValue, setInputValue] = useState<string>('');
    // Track the source of input to determine whether to show search dropdown
    // 'keyboard' and 'paste' trigger dropdown, 'dialpad' does not
    const [inputSource, setInputSource] = useState<InputSource>('keyboard');
    const { t } = useTranslation('WebexSearchContacts');
    const isFeatureParityEnabled =
      enableFeatureParitySearch &&
      isMobileFeatureParityEnabledForCurrentUser();
    const placeholder =
      label ||
      t(isFeatureParityEnabled ? 'search' : 'mobile.dialNumber');

    const [clickRef, showDropdown] = useSearchDropdown<HTMLDivElement>(
      inputValue,
      minSearchLength!,
      inputSource
    );
    const dropdownRef = useRef<HTMLDivElement>(null); 
    const backSpaceAction = () => {
      // Backspace triggered from dialpad button should set source as 'dialpad'
      // This prevents the dropdown from staying open incorrectly
      setInputSource('dialpad');
      setInputValue((prev) => prev.slice(0, -1));
    }; 
    const callButtonPressAction = () => {
      setInputValue('');
    };
    const clearInput = () => {
      setInputValue('');
    };
    
    useImperativeHandle(ref, () => ({
      appendValueToInput(toAppend: string, source: InputSource = 'keyboard') {
        // Set the input source before updating the value
        // This ensures the dropdown behavior is determined by the source
        setInputSource(source);
        setInputValue((prev) => prev + toAppend);
      },
      backSpaceAction,
      getInputValue() {
        return inputValue;
      }, 
      callButtonPressAction,
      clearInput,
      focusInput() {
        clickRef.current?.querySelector('input')?.focus();
      },
      blurInput() {
        clickRef.current?.querySelector('input')?.blur();
      },
    }));

    useEffect(() => {
      if (!showDropdown && onDropdownHide) {
        onDropdownHide();
      }
    }, [showDropdown, onDropdownHide]);

    useEffect(() => {
      if (onInputChange) {
        onInputChange(inputValue);
      }
    }, [inputValue, onInputChange]);

  

    const isClearControl = (target: EventTarget | null) => {
      const node = target instanceof Element ? target : null;
      const button = node?.closest('button');
      return Boolean(
        button &&
        (button.classList.contains('clear-icon') ||
          button.getAttribute('aria-label') === t('clearButton'))
      );
    };

    const handleClearPointerDownCapture = (
      event: React.PointerEvent<HTMLDivElement>
    ) => {
      if (!isClearControl(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setInputSource('keyboard');
      setInputValue('');
    };

    const handleEnterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.keyCode === 13) {
        if (inputValue) {
          if(makeCall){
            makeCall(inputValue, false);
            setInputValue('')
          }
        }
      }
      if (event.key === 'Escape' || event.keyCode === 27) {
        setInputValue('')
      }
    };

    return (
      <div
        className={cssClasses}
        style={style}
        ref={clickRef}
        onPointerDownCapture={handleClearPointerDownCapture}
      >
        <TextInput
          className={sc('input')}
          placeholder={placeholder}
          aria-label={placeholder}
          value={inputValue}
          onChange={(value) => {
            // When user types directly in the input, it's keyboard input
            // This should trigger the search dropdown
            setInputSource('keyboard');
            setInputValue(value);
          }}
          autoComplete="off"
          onKeyDown={handleEnterKeyPress}
          clearAriaLabel={t('clearButton')}
        />
        {showDropdown && isFeatureParityEnabled && (
          <div
            className={sc('dropdown')}
            data-testid="webex-search-contacts-dropdown"
          >
            <WebexSearchContactsList
              clearSearchInput={callButtonPressAction}
              searchValue={inputValue}
              noContactsFoundMessage={
                noContactsFoundMessage || t('noContactsFound')
              }
              onUserSelect={onUserSelect}
              hideSource={hideDropdownSource}
              makeCall={makeCall}
            />
          </div>
        )}
      </div>
    );
  }
);

WebexMobileSearchContacts.defaultProps = {
  label: undefined,
  noContactsFoundMessage: undefined,
  style: undefined,
  minSearchLength: 1,
  onInputChange: () => { },
  onUserSelect: () => { },
  onDropdownHide: () => { },
  hideDropdownSource: false,
  enableFeatureParitySearch: true,
};
